import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { missionErrorMessage } from "../../lib/missions";
import { StatePill } from "../CollaboratorBits";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Skeleton } from "../ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "sonner";
import { AlertCircle, ChevronRight, Loader2, Plus } from "lucide-react";

// Organisation › Modèles de compte rendu (Ordre 15): what the team fills in after a mission, per mission type. Versioned like the
// procedures (same editor): a report keeps the version it started with. Written by whoever manages mission types.
export default function ReportTemplatesPanel() {
  const navigate = useNavigate();
  const [state, setState] = useState({ phase: "loading", items: [], error: "" });
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/report-templates");
      setState({ phase: "ready", items: data.items, error: "" });
    } catch (e) {
      setState({ phase: "error", items: [], error: missionErrorMessage(e, "Impossible de charger les modèles de compte rendu.") });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/report-templates", { label: label.trim(), description: description.trim() || null });
      toast.success("Modèle créé : composez son premier brouillon");
      navigate(`/missions/modeles-compte-rendu/${data.id}`);
    } catch (e) {
      toast.error(missionErrorMessage(e, "Impossible de créer le modèle."));
    } finally {
      setBusy(false);
    }
  };

  if (state.phase === "loading") return <Skeleton className="h-40 w-full" />;
  if (state.phase === "error") {
    return (
      <Card className="p-8 text-center space-y-3" data-testid="report-templates-error">
        <AlertCircle className="mx-auto text-slate-300" />
        <p className="text-sm text-slate-600">{state.error}</p>
        <Button variant="outline" onClick={load}>Réessayer</Button>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="report-templates-panel">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-slate-500">Ce que l'équipe remplit après une mission. Chaque type de mission choisit le sien ; sans choix, c'est le modèle standard.</p>
        <Button onClick={() => { setLabel(""); setDescription(""); setOpen(true); }} className="shrink-0" data-testid="report-templates-new">
          <Plus size={16} className="mr-1.5" />Nouveau modèle
        </Button>
      </div>
      <Card className="divide-y divide-slate-100 overflow-hidden" data-testid="report-templates-list">
        {state.items.map((t) => (
          <Link key={t.id} to={`/missions/modeles-compte-rendu/${t.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50" data-testid={`report-template-row-${t.code}`}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-slate-900 break-words">{t.label}</span>
                <StatePill active={t.active} activeLabel="Actif" inactiveLabel="Désactivé" />
                {t.standard && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">par défaut</span>}
              </div>
              <div className="text-xs text-slate-500">
                {t.current_version ? `Version ${t.current_version.number} publiée` : "Jamais publié"}
                {t.draft_version ? ` · brouillon v${t.draft_version.number} en cours` : ""}
                {t.description ? ` · ${t.description}` : ""}
              </div>
            </div>
            <ChevronRight size={16} className="text-slate-300 shrink-0" />
          </Link>
        ))}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="report-template-create-dialog">
          <DialogHeader>
            <DialogTitle>Nouveau modèle de compte rendu</DialogTitle>
            <DialogDescription>Vous le composerez ensuite, puis le publierez avant de le choisir pour un type de mission.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label htmlFor="rt-label">Nom *</Label>
              <Input id="rt-label" maxLength={120} className="text-base md:text-sm" placeholder="Ex. Compte rendu d'installation" value={label}
                onChange={(e) => setLabel(e.target.value)} data-testid="report-template-create-label" /></div>
            <div className="space-y-1.5"><Label htmlFor="rt-description">Description</Label>
              <Textarea id="rt-description" rows={2} maxLength={2000} className="text-base md:text-sm" value={description} onChange={(e) => setDescription(e.target.value)} /></div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Annuler</Button>
            <Button onClick={create} disabled={busy || !label.trim()} data-testid="report-template-create-confirm">
              {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
